import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SurveyFieldGroup } from './types';
import {
  fetchFieldGroups,
  createFieldGroup,
  updateFieldGroup,
  deleteFieldGroup,
  reorderFieldGroups,
} from './surveyFieldGroupsApi';

interface SurveyFieldGroupsState {
  items: SurveyFieldGroup[];
  loading: boolean;
  error: string | null;
}

const initialState: SurveyFieldGroupsState = {
  items: [],
  loading: false,
  error: null,
};

export const loadFieldGroups = createAsyncThunk(
  'surveyFieldGroups/load',
  async ({ surveyId }: { surveyId: string }) => {
    return await fetchFieldGroups(surveyId);
  }
);

export const addFieldGroup = createAsyncThunk(
  'surveyFieldGroups/add',
  async ({
    surveyId,
    payload,
  }: {
    surveyId: string;
    payload: { name: string; weight: number; order?: number };
  }) => {
    return await createFieldGroup(surveyId, payload);
  }
);

export const editFieldGroup = createAsyncThunk(
  'surveyFieldGroups/edit',
  async ({
    surveyId,
    groupId,
    payload,
  }: {
    surveyId: string;
    groupId: string;
    payload: { name?: string; weight?: number; order?: number };
  }) => {
    return await updateFieldGroup(surveyId, groupId, payload);
  }
);

export const removeFieldGroup = createAsyncThunk(
  'surveyFieldGroups/remove',
  async ({
    surveyId,
    groupId,
  }: {
    surveyId: string;
    groupId: string;
  }) => {
    await deleteFieldGroup(surveyId, groupId);
    return groupId;
  }
);

export const moveFieldGroup = createAsyncThunk(
  'surveyFieldGroups/reorder',
  async ({
    surveyId,
    groups,
  }: {
    surveyId: string;
    groups: { id: string; order: number }[];
  }) => {
    await reorderFieldGroups(surveyId, groups);
    return groups;
  }
);

const surveyFieldGroupsSlice = createSlice({
  name: 'surveyFieldGroups',
  initialState,
  reducers: {
    clearFieldGroups(state) {
      state.items = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFieldGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadFieldGroups.fulfilled, (state, action: PayloadAction<SurveyFieldGroup[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(loadFieldGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar grupos de campos';
      })
      .addCase(addFieldGroup.fulfilled, (state, action: PayloadAction<SurveyFieldGroup>) => {
        state.items.push(action.payload);
        state.items.sort((a, b) => a.order - b.order);
      })
      .addCase(editFieldGroup.fulfilled, (state, action: PayloadAction<SurveyFieldGroup>) => {
        const index = state.items.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
          state.items.sort((a, b) => a.order - b.order);
        }
      })
      .addCase(removeFieldGroup.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(moveFieldGroup.fulfilled, (state, action: PayloadAction<{ id: string; order: number }[]>) => {
        action.payload.forEach((update) => {
          const item = state.items.find((i) => i.id === update.id);
          if (item) {
            item.order = update.order;
          }
        });
        state.items.sort((a, b) => a.order - b.order);
      });
  },
});

export const { clearFieldGroups } = surveyFieldGroupsSlice.actions;
export default surveyFieldGroupsSlice.reducer;
